/**
 * pendingRoute — remembers where a tap was TRYING to go, so the auth gate
 * can't swallow it.
 *
 * The bug this exists for (founder report 2026-08-12): tapping a "Concierge ·
 * Markus" push opened the SIGN-IN screen. Two things combined —
 *
 *   1. the Concierge push hard-navigated (`window.location.assign`), which
 *      reloads the whole WebView and re-runs the cold-start auth gate; and
 *   2. `UnauthenticatedApp`'s catch-all is `<Navigate to="/login" replace />`,
 *      which REPLACES the URL. The instant the gate rendered without a session
 *      — a transient GoTrue storage read on a cold WKWebView is enough — the
 *      intended destination was overwritten and gone. Even after the session
 *      resolved a beat later there was nothing left to go back to.
 *
 * Fix (1) is in push.ts (navigate in-SPA, no reload). This module is fix (2):
 * the destination is stashed before any redirect and replayed once we're
 * authenticated, so a lost race costs a moment, not the destination. It also
 * means a genuinely signed-out user who taps a link lands where they meant to
 * go after signing in, instead of on My Day.
 *
 * Durable (localStorage, not sessionStorage) because the whole point is
 * surviving a reload, and iOS WebViews do not reliably carry sessionStorage
 * across a cold launch.
 */

const KEY = "trybeup_pending_route";

/** Beyond this, a stashed route is assumed abandoned rather than pending. */
export const PENDING_ROUTE_TTL_MS = 30 * 60 * 1000;

/**
 * Routes with nothing to restore. Stashing these would mean a later replay
 * yanks the user off whatever they're doing to land them... where they already
 * are.
 */
const NOT_WORTH_RESTORING = new Set(["/", "/login", "/reset-password"]);

/**
 * Query params that make a URL a one-time auth artefact rather than a
 * destination. A PKCE `?code=` is single-use and belongs to the sign-in that
 * is happening right now — writing it to durable storage for half an hour, and
 * then navigating back to it later, is both pointless and a credential sitting
 * somewhere it was never meant to live. `useAuth`'s own comment already warns
 * that the unauthenticated catch-all can fire mid-callback, which is exactly
 * when this would be reached.
 */
const AUTH_CALLBACK_PARAMS = ["code", "access_token", "refresh_token", "token", "error"];

/**
 * Is this a safe in-app destination? Deliberately strict: this value gets fed
 * to `navigate()`, so anything that could read as an absolute or
 * protocol-relative URL is refused rather than sanitised.
 */
export function isRestorablePath(path: string): boolean {
  if (typeof path !== "string" || path.length === 0 || path.length > 512) return false;
  if (!path.startsWith("/")) return false;
  // `//evil.com` is protocol-relative — an off-site redirect wearing a path's
  // clothes. Backslashes are refused for the same reason: several URL parsers
  // normalise `\` to `/`, so `/\evil.com` can escape too.
  if (path.startsWith("//") || path.includes("\\")) return false;
  const [pathname] = path.split(/[?#]/, 1);
  if (NOT_WORTH_RESTORING.has(pathname)) return false;

  const qIndex = path.indexOf("?");
  if (qIndex >= 0) {
    const params = new URLSearchParams(path.slice(qIndex + 1));
    if (AUTH_CALLBACK_PARAMS.some((p) => params.has(p))) return false;
  }
  return true;
}

/** Remember a destination to replay once we're through the auth gate. */
export function stashPendingRoute(path: string, now: number = Date.now()): void {
  if (!isRestorablePath(path)) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ path, at: now }));
  } catch {
    /* storage blocked — the deep link is simply not restored */
  }
}

/**
 * Read and clear the stashed destination. Returns null when there isn't one,
 * when it's older than the TTL, or when the stored blob is unreadable.
 *
 * Always clears, including on the expired/corrupt paths — a stash we refuse to
 * act on must not sit there waiting to hijack a later launch.
 */
export function takePendingRoute(now: number = Date.now()): string | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
    if (raw !== null) localStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown };
    const path = typeof parsed.path === "string" ? parsed.path : null;
    const at = typeof parsed.at === "number" ? parsed.at : 0;
    if (!path || !isRestorablePath(path)) return null;
    if (!Number.isFinite(at) || now - at > PENDING_ROUTE_TTL_MS) return null;
    return path;
  } catch {
    return null;
  }
}
